import { useColorScheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import LightModeIcon from '@mui/icons-material/LightMode';
import { styled, keyframes } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';

const ANIMATION_TIMEOUT = 150;

const iconContainerSx = {
  position: 'relative',
  width: 24,
  height: 24,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const StyledLightModeIcon = styled(LightModeIcon)({ color: '#FFA000' });
const StyledBedtimeIcon = styled(BedtimeIcon)({ color: '#D1C4E9' });

const exitLeft = keyframes`
  from { transform: translateX(0) rotate(0deg); opacity: 1; }
  to { transform: translateX(-24px) rotate(-90deg); opacity: 0; }
`;
const exitRight = keyframes`
  from { transform: translateX(0) rotate(0deg); opacity: 1; }
  to { transform: translateX(24px) rotate(90deg); opacity: 0; }
`;
const enterFromRight = keyframes`
  from { transform: translateX(24px) rotate(90deg); opacity: 0; }
  to { transform: translateX(0) rotate(0deg); opacity: 1; }
`;
const enterFromLeft = keyframes`
  from { transform: translateX(-24px) rotate(-90deg); opacity: 0; }
  to { transform: translateX(0) rotate(0deg); opacity: 1; }
`;

const AnimatedIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'animating' && prop !== 'direction' && prop !== 'isCurrent',
})<{
  animating: boolean;
  direction: 'toLight' | 'toDark';
  isCurrent: boolean;
}>(({ animating, direction, isCurrent }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  inset: 0,
  margin: 'auto',
  animation: animating
    ? isCurrent
      ? `${direction === 'toDark' ? exitLeft : exitRight} 0.3s ease forwards`
      : `${direction === 'toDark' ? enterFromRight : enterFromLeft} 0.3s ease forwards`
    : 'none',
}));

export function useThemeToggle() {
  const { mode, setMode } = useColorScheme();
  const isDark = mode === 'dark';
  const toggle = () => setMode(isDark ? 'light' : 'dark');
  const Icon = isDark ? StyledBedtimeIcon : StyledLightModeIcon;
  return { isDark, toggle, Icon };
}

export const ThemeToggleButton = () => {
  const { mode, setMode } = useColorScheme();
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'toLight' | 'toDark'>('toDark');
  const isDark = mode === 'dark';

  const handleToggle = useCallback(() => {
    if (animating) return;
    const newDirection = isDark ? 'toLight' : 'toDark';
    setDirection(newDirection);
    setAnimating(true);

    setTimeout(() => {
      requestAnimationFrame(() => {
        setMode(newDirection === 'toDark' ? 'dark' : 'light');
        setAnimating(false);
      });
    }, ANIMATION_TIMEOUT);
  }, [animating, isDark, setMode]);

  const CurrentIcon = isDark ? StyledBedtimeIcon : StyledLightModeIcon;
  const NextIcon = isDark ? StyledLightModeIcon : StyledBedtimeIcon;

  return (
    <IconButton onClick={handleToggle} sx={{ p: 0.75 }}>
      <Box sx={iconContainerSx}>
        <AnimatedIcon animating={animating} direction={direction} isCurrent={true}>
          <CurrentIcon />
        </AnimatedIcon>
        {animating && (
          <AnimatedIcon animating={animating} direction={direction} isCurrent={false}>
            <NextIcon />
          </AnimatedIcon>
        )}
      </Box>
    </IconButton>
  );
};
